<template>
  <div class="grain-overlay" aria-hidden="true"></div>
  <LoadingScreen :isLoading="isLoading" />

  <div v-show="!isLoading" class="relative min-h-screen">

    <div class="navbar-wrapper">
      <nav class="navbar" :class="{ 'navbar--scrolled': hasScrolled, 'navbar--dark-page': isDarkPage }">
        <div class="navbar__inner">

          <!-- ── LOGO ── -->
          <router-link to="/" class="navbar__logo">
            <img src="./assets/logo-beli.png" alt="Barbers Unity" class="navbar__logo-img">
          </router-link>

          <!-- ── DESKTOP LINKS ── -->
          <ul class="navbar__links">
            <li><router-link to="/about" class="navbar__link">{{ $t('navigation.aboutUs') }}</router-link></li>
            <li><router-link to="/pricing" class="navbar__link">{{ $t('navigation.pricing') || 'Pricing'
            }}</router-link></li>
            <li><router-link to="/contact" class="navbar__link">{{ $t('navigation.contactUs') }}</router-link></li>
          </ul>

          <!-- ── DESKTOP RIGHT ── -->
          <div class="navbar__right">

            <!-- Language Picker -->
            <div class="lang-picker" ref="langPicker">
              <button class="lang-btn" @click.stop="toggleLanguageDropdown">
                <span>{{ flags[currentLanguage] || '🌐' }}</span>
                <svg class="lang-chevron" :class="{ 'is-open': showLanguageDropdown }" width="10" height="10"
                  viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              <transition name="drop">
                <div v-if="showLanguageDropdown" class="lang-dropdown">
                  <button v-for="lang in availableLanguages" :key="lang" class="lang-option"
                    :class="{ 'is-active': currentLanguage === lang }" @click="changeLanguage(lang)">
                    <span>{{ flags[lang] }}</span>
                    <span>{{ lang }}</span>
                    <svg v-if="currentLanguage === lang" class="lang-tick" width="11" height="11" viewBox="0 0 24 24"
                      fill="none" stroke="currentColor" stroke-width="3">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </button>
                </div>
              </transition>
            </div>

            <!-- STATIC LINKS TO MAIN APP -->
            <template v-if="isLoggedIn">
              <button v-if="isCustomer" @click="isCustomerDrawerOpen = true" class="btn-profile-icon"
                aria-label="My account">
                <i class="fa fa-user-circle"></i>
              </button>
              <a v-else href="https://book.barbersunity.com/admin" class="btn-profile-icon" aria-label="Dashboard">
                <i class="fa fa-user-circle"></i>
              </a>
            </template>
            <a v-else href="https://book.barbersunity.com/admin" class="btn-ghost">
              {{ $t('navigation.login') || 'Log in' }}
            </a>

            <a href="https://book.barbersunity.com/eng/register" class="btn-primary">
              {{ $t('navigation.joinAsBarber') || 'Join as Barber' }}
            </a>

            <!-- CTA Menu -->
            <div class="cta-group" ref="ctaGroup">
              <button class="cta-btn-icon" @mouseenter="showCtaMenu = true" @click="showCtaMenu = !showCtaMenu"
                aria-label="More options">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                  stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="1" />
                  <circle cx="19" cy="12" r="1" />
                  <circle cx="5" cy="12" r="1" />
                </svg>
              </button>
              <transition name="drop">
                <div v-if="showCtaMenu" class="cta-dropdown" @mouseenter="showCtaMenu = true"
                  @mouseleave="showCtaMenu = false">
                  <router-link to="/map" class="cta-item">
                    <div class="cta-item__icon">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                        stroke-linecap="round" stroke-linejoin="round">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                    </div>
                    <div>
                      <div class="cta-item__label">Book as Customer</div>
                      <div class="cta-item__sub">Find & book your barber on the map</div>
                    </div>
                  </router-link>

                  <div class="cta-sep"></div>

                  <a href="https://demo.barbersunity.com" class="cta-item cta-item--muted">
                    <div class="cta-item__icon">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                        stroke-linecap="round" stroke-linejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <polygon points="10 8 16 12 10 16 10 8" />
                      </svg>
                    </div>
                    <div>
                      <div class="cta-item__label">{{ $t('navigation.demo') || 'See demo' }}</div>
                      <div class="cta-item__sub">Live preview, no sign-up needed</div>
                    </div>
                  </a>
                </div>
              </transition>
            </div>

          </div>

          <!-- ── MOBILE RIGHT ── -->
          <div class="navbar__mobile-right">

            <template v-if="isLoggedIn">
              <button v-if="isCustomer" @click="isCustomerDrawerOpen = true" class="mobile-login-btn">
                <i class="fa fa-user-circle"></i>
              </button>
              <a v-else href="https://book.barbersunity.com/admin" class="mobile-login-btn">
                <i class="fa fa-user-circle"></i>
              </a>
            </template>
            <a v-else href="https://book.barbersunity.com/admin" class="mobile-login-btn">
              {{ $t('navigation.login') || 'Log in' }}
            </a>

            <!-- Lang Picker -->
            <div class="lang-picker" ref="langPickerMobile">
              <button class="lang-btn" @click.stop="toggleLanguageDropdown">
                <span>{{ flags[currentLanguage] || '🌐' }}</span>
              </button>
              <transition name="drop">
                <div v-if="showLanguageDropdown" class="lang-dropdown lang-dropdown--right">
                  <button v-for="lang in availableLanguages" :key="lang" class="lang-option"
                    :class="{ 'is-active': currentLanguage === lang }" @click="changeLanguage(lang)">
                    <span>{{ flags[lang] }}</span>
                    <span>{{ lang }}</span>
                  </button>
                </div>
              </transition>
            </div>

            <!-- Hamburger -->
            <button class="hamburger" :class="{ 'is-open': isMenuOpen }" @click="toggleMenu">
              <span class="hamburger__bar"></span>
              <span class="hamburger__bar"></span>
            </button>
          </div>

        </div>

        <!-- ── MOBILE DRAWER ── -->
        <transition name="drawer">
          <div v-if="isMenuOpen" class="mobile-drawer">
            <router-link to="/about" class="drawer-link" @click="closeAllMenus">{{ $t('navigation.aboutUs')
            }}</router-link>
            <router-link to="/pricing" class="drawer-link" @click="closeAllMenus">{{ $t('navigation.pricing') ||
              'Pricing' }}</router-link>
            <router-link to="/contact" class="drawer-link" @click="closeAllMenus">{{ $t('navigation.contactUs')
            }}</router-link>
            <router-link to="/map" class="drawer-link" @click="closeAllMenus">{{ $t('nav.findBarber') || 'Find a Barber'
            }}</router-link>

            <div class="drawer-sep"></div>

            <template v-if="isLoggedIn">
              <button v-if="isCustomer" @click="isCustomerDrawerOpen = true; closeAllMenus()"
                class="drawer-link drawer-link--dim">My Account</button>
              <a v-else href="https://book.barbersunity.com/admin" class="drawer-link drawer-link--dim"
                @click="closeAllMenus">Dashboard</a>
            </template>
            <a v-else href="https://book.barbersunity.com/admin" class="drawer-link drawer-link--dim"
              @click="closeAllMenus">{{ $t('navigation.login') || 'Log in' }}</a>
            <a href="https://book.barbersunity.com/eng/register" class="drawer-link drawer-link--blue"
              @click="closeAllMenus">{{ $t('navigation.joinAsBarber') || 'Join as Barber' }}</a>
            <a href="https://book.barbersunity.com/eng/register-customer" class="drawer-link drawer-link--blue"
              @click="closeAllMenus">Book as Customer</a>
          </div>
        </transition>
      </nav>
    </div>

    <!-- ── FLOATING BUTTONS ── -->
    <a v-show="hasScrolled" href="https://wa.me/359876566094" target="_blank" class="whatsapp-float">
      <svg width="21" height="21" viewBox="0 0 24 24" fill="currentColor">
        <path
          d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
      </svg>
    </a>

    <transition name="slide-up">
      <div v-if="showMobileCta" class="mobile-sticky">
        <a href="https://book.barbersunity.com/eng/register" class="mobile-sticky__btn">
          {{ $t('navigation.joinAsBarber') }}
        </a>
      </div>
    </transition>

    <button v-show="showScrollTop" @click="scrollToTop" class="scroll-top" aria-label="Back to top">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
        stroke-linecap="round" stroke-linejoin="round">
        <path d="M5 10l7-7 7 7" />
        <path d="M12 3v18" />
      </svg>
    </button>

    <!-- ── MAIN CONTENT ── -->
    <main>
      <router-view v-slot="{ Component, route }">
        <transition name="page-fade" mode="out-in">
          <div :key="route.path">
            <component :is="Component" v-if="Component" />
          </div>
        </transition>
      </router-view>
    </main>

    <!-- ── FOOTER ── -->
    <footer class="site-footer">
      <div class="footer-inner">
        <div class="footer-grid">
          <div>
            <img src="./assets/logo-beli.png" class="footer-logo" alt="Barbers Unity">
            <p class="footer-desc">{{ $t('footer.description') }}</p>
            <a href="https://book.barbersunity.com/eng/register" class="footer-cta-link">{{
              $t('navigation.joinAsBarber') }}</a>
          </div>
          <div>
            <p class="footer-heading">{{ $t('footer.quickLinks.title') }}</p>
            <router-link to="/about" class="footer-link">{{ $t('footer.quickLinks.about') }}</router-link>
            <router-link to="/pricing" class="footer-link">{{ $t('navigation.pricing') || 'Pricing' }}</router-link>
            <router-link to="/contact" class="footer-link">{{ $t('footer.quickLinks.contact') }}</router-link>
            <a href="https://demo.barbersunity.com/" class="footer-link">{{ $t('footer.quickLinks.demo') }}</a>
          </div>
          <div>
            <p class="footer-heading">{{ $t('footer.legal.title') }}</p>
            <router-link to="/privacy" class="footer-link">{{ $t('footer.legal.privacy') }}</router-link>
            <router-link to="/terms" class="footer-link">{{ $t('footer.legal.terms') }}</router-link>
          </div>
          <div>
            <p class="footer-heading">{{ $t('footer.followUs') }}</p>
            <div class="footer-social">
              <a href="https://www.facebook.com/profile.php?id=61575548669877"><i class="fab fa-facebook"></i></a>
              <a href="https://www.instagram.com/withbarbersunity/"><i class="fab fa-instagram"></i></a>
              <a href="https://www.tiktok.com/@barbersunity"><i class="fa-brands fa-tiktok"></i></a>
            </div>
            <template v-if="isLoggedIn">
              <button v-if="isCustomer" @click="isCustomerDrawerOpen = true" class="footer-login-link">
                <i class="fas fa-user-circle"></i> My Account
              </button>
              <a v-else href="https://book.barbersunity.com/admin" class="footer-login-link">
                <i class="fas fa-user-circle"></i> Dashboard
              </a>
            </template>
            <a v-else href="https://book.barbersunity.com/admin" class="footer-login-link">
              <i class="fas fa-sign-in-alt"></i> {{ $t('navigation.login') || 'Barber Login' }}
            </a>
          </div>
        </div>
        <div class="footer-bottom">
          <p>{{ $t('footer.copyright') }}</p>
        </div>
      </div>
    </footer>

    <!-- ── COOKIES ── -->
    <transition enter-active-class="transition duration-500 ease-out"
      enter-from-class="transform translate-y-full opacity-0" enter-to-class="transform translate-y-0 opacity-100"
      leave-active-class="transition duration-300 ease-in" leave-from-class="transform translate-y-0 opacity-100"
      leave-to-class="transform translate-y-full opacity-0">
      <div v-if="showCookieConsent" class="cookie-bar">
        <div class="cookie-inner">
          <div class="cookie-text">
            <strong>{{ $t('cookies.title') || 'Cookie Preferences' }}</strong>
            <span>{{ $t('cookies.message') || 'We use cookies to enhance your experience.' }} <router-link
                to="/privacy">{{ $t('footer.legal.privacy') || 'Learn more' }}</router-link>.</span>
          </div>
          <div class="cookie-actions">
            <button @click="declineCookies" class="cookie-btn cookie-btn--ghost">{{ $t('cookies.decline') || 'Decline'
            }}</button>
            <button @click="acceptCookies" class="cookie-btn cookie-btn--solid">{{ $t('cookies.accept') || 'Accept'
            }}</button>
          </div>
        </div>
      </div>
    </transition>

    <RegistrationModal :isOpen="isRegistrationModalOpen" @close="isRegistrationModalOpen = false" />
    <CustomerDrawer :isOpen="isCustomerDrawerOpen" @close="isCustomerDrawerOpen = false" />
  </div>
</template>

<script>
import '@/assets/animations.css'
import LoadingScreen from '@/components/LoadingScreen.vue'
import RegistrationModal from '@/components/RegistrationModal.vue'
import CustomerDrawer from '@/components/CustomerDrawer.vue'
import { Auth } from '@/services/index.js'
import { i18n } from '@/utils/i18n'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

export default {
  name: 'App',
  components: { LoadingScreen, RegistrationModal, CustomerDrawer },
  data() {
    return {
      currentLanguage: i18n.locale,
      availableLanguages: ['ENG', 'BG', 'ALB', 'HR', 'GER', 'SLO', 'FR'],
      flags: { ENG: '🇬🇧', BG: '🇧🇬', ALB: '🇦🇱', HR: '🇭🇷', GER: '🇩🇪', SLO: '🇸🇮', FR: '🇫🇷' },
      showLanguageDropdown: false,
      showCtaMenu: false,
      isMenuOpen: false,
      isLoading: true,
      showScrollTop: false,
      showCookieConsent: false,
      hasScrolled: false,
      showMobileCta: false,
      isRegistrationModalOpen: false,
      isCustomerDrawerOpen: false,
      user: null
    }
  },
  computed: {
    isDarkPage() {
      return ['/about', '/pricing', '/contact'].includes(this.$route.path);
    },
    isLoggedIn() {
      return !!this.user
    },
    isCustomer() {
      return !!this.user?.isCustomer
    }
  },
  methods: {
    openRegistrationModal() { this.isMenuOpen = false; this.isRegistrationModalOpen = true },
    toggleMenu() { this.isMenuOpen = !this.isMenuOpen; if (this.isMenuOpen) this.showLanguageDropdown = false },
    toggleLanguageDropdown(e) { e.stopPropagation(); this.showLanguageDropdown = !this.showLanguageDropdown },
    changeLanguage(lang) { this.currentLanguage = lang; i18n.locale = lang; localStorage.setItem('language', lang); this.showLanguageDropdown = false },
    handleClickOutside(e) {
      if (!e.target.closest('.lang-picker')) this.showLanguageDropdown = false
      if (!e.target.closest('.cta-group')) this.showCtaMenu = false
      if (!e.target.closest('nav')) this.isMenuOpen = false
    },
    closeAllMenus() { this.isMenuOpen = false; this.showLanguageDropdown = false; this.showCtaMenu = false },
    scrollToTop() { window.scrollTo({ top: 0, behavior: 'smooth' }) },
    handleScroll() {
      const y = window.scrollY, wh = window.innerHeight, dh = document.documentElement.scrollHeight
      this.hasScrolled = y > 50
      this.showScrollTop = (dh - (y + wh)) < 100
      this.showMobileCta = y > wh * 0.6 && (dh - (y + wh)) > 200
    },
    beforeEnter(el) { el.style.opacity = 0; el.style.transform = 'translateY(16px)' },
    enter(el, done) {
      let called = false
      const finish = () => {
        if (called) return
        called = true
        el.style.opacity = 1
        el.style.transform = 'translateY(0px)'
        ScrollTrigger.refresh(true);
        done()
      }
      gsap.to(el, { duration: 0.55, opacity: 1, y: 0, ease: 'power2.out', onComplete: finish })
      setTimeout(finish, 900)
    },
    afterEnter(el) { el.style.opacity = ''; el.style.transform = '' },
    beforeLeave(el) { el.style.opacity = 1 },
    leave(el, done) {
      let called = false
      const finish = () => {
        if (called) return
        called = true
        el.style.opacity = 0
        done()
      }
      gsap.to(el, { duration: 0.35, opacity: 0, y: -14, ease: 'power2.in', onComplete: finish })
      setTimeout(finish, 700)
    },
    afterLeave(el) { el.style.transform = '' },
    acceptCookies() { this.showCookieConsent = false; localStorage.setItem('cookieConsent', 'accepted') },
    declineCookies() { this.showCookieConsent = false; localStorage.setItem('cookieConsent', 'declined') },
  },
  watch: {
    $route() {
      this.closeAllMenus()
      window.scrollTo(0, 0)
      this.$nextTick(() => {
        setTimeout(() => { ScrollTrigger.refresh() }, 350)
      })
    }
  },
  mounted() {
    const hide = () => setTimeout(() => { this.isLoading = false }, 500)
    if (document.readyState === 'complete') hide(); else window.addEventListener('load', hide)
    document.addEventListener('click', this.handleClickOutside)
    window.addEventListener('scroll', this.handleScroll, { passive: true })
    if (!localStorage.getItem('cookieConsent')) setTimeout(() => { this.showCookieConsent = true }, 1200)
    this.user = Auth.getUser()
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
    window.removeEventListener('scroll', this.handleScroll)
  }
}
</script>

<style scoped>
/* ALL STYLING REMAINS EXACTLY THE SAME AS YOUR PREVIOUS FILE */
.grain-overlay {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 9998;
  opacity: 0.022;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
  background-size: 180px;
}

.page-fade-enter-active,
.page-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.page-fade-enter-from {
  opacity: 0;
  transform: translateY(12px);
}

.page-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* PILL NAVBAR */
.navbar-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 50;
  display: flex;
  justify-content: center;
  padding-top: 1.5rem;
}

.navbar {
  position: fixed;
  width: 90%;
  max-width: 1200px;
  border-radius: 999px;
  background: rgba(71, 78, 89, 0.15);
  backdrop-filter: blur(16px) saturate(180%);
  -webkit-backdrop-filter: blur(16px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  transition: all 0.35s ease;
}

.navbar--scrolled {
  background: rgba(15, 20, 30, 0.85);
  border-color: rgba(255, 255, 255, 0.15);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
  transform: translateY(-0.5rem);
}

.navbar__inner {
  padding: 0 1.5rem 0 2rem;
  height: 64px;
  display: flex;
  align-items: center;
}

.mobile-login-btn {
  font-size: 0.8rem;
  font-weight: 600;
  color: rgba(230, 240, 250, 0.9);
  text-decoration: none;
  padding: 0.35rem 0.8rem;
  border-radius: 50px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  transition: all 0.2s;
  white-space: nowrap;
}

.mobile-login-btn:hover {
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
}

.navbar:not(.navbar--scrolled) .mobile-login-btn {
  color: #1e293b;
  background: rgba(30, 41, 59, 0.05);
  border-color: rgba(30, 41, 59, 0.1);
}

@media (max-width: 860px) {
  .navbar__inner {
    padding: 0 1.25rem;
  }

  .navbar {
    border-radius: 20px;
    width: 95%;
  }
}

/* Logo */
.navbar__logo {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  opacity: 1;
  transition: opacity 0.2s;
}

.navbar__logo:hover {
  opacity: 0.7;
}

.navbar:not(.navbar--scrolled) .navbar__logo-img {
  filter: brightness(0.1);
}

.navbar__logo-img {
  height: 28px;
  width: auto;
  display: block;
}

/* Links */
.navbar__links {
  display: flex;
  align-items: center;
  list-style: none;
  padding: 0;
  margin: 0 0 0 2rem;
  gap: 0.5rem;
  flex: 1;
}

.navbar__link {
  position: relative;
  display: block;
  padding: 0.4rem 0.8rem;
  font-size: 0.875rem;
  font-weight: 500;
  letter-spacing: 0.01em;
  color: rgba(230, 240, 250, 0.7);
  text-decoration: none;
  transition: color 0.35s ease;
  white-space: nowrap;
}

.navbar__link:hover,
.navbar__link.router-link-active {
  color: #fff;
}

.navbar:not(.navbar--scrolled) .navbar__link {
  color: rgba(30, 41, 59, 0.7);
}

.navbar:not(.navbar--scrolled) .navbar__link:hover,
.navbar:not(.navbar--scrolled) .navbar__link.router-link-active {
  color: #0f172a;
}

/* Right cluster */
.navbar__right {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  margin-left: auto;
}

.btn-ghost {
  font-size: 0.875rem;
  font-weight: 600;
  color: rgba(230, 240, 250, 0.85);
  text-decoration: none;
  padding: 0.4rem 1rem;
  border-radius: 50px;
  border: 1px solid transparent;
  transition: all 0.35s ease;
}

.btn-ghost:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}

.navbar:not(.navbar--scrolled) .btn-ghost {
  color: rgba(30, 41, 59, 0.85);
  border-color: rgba(30, 41, 59, 0.2);
  background: rgba(255, 255, 255, 0.2);
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 1.25rem;
  background: #4a7fa5;
  color: #fff;
  font-size: 0.875rem;
  font-weight: 600;
  text-decoration: none;
  border-radius: 50px;
  transition: all 0.2s;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.btn-primary:hover {
  background: #5a90b8;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(74, 127, 165, 0.3);
}

.cta-btn-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: rgba(230, 240, 250, 0.7);
  cursor: pointer;
  transition: all 0.35s ease;
}

.cta-btn-icon:hover {
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
}

.btn-profile-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: rgba(230, 240, 250, 0.9);
  cursor: pointer;
  text-decoration: none;
  transition: all 0.2s;
  font-size: 1.1rem;
}

.btn-profile-icon:hover {
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
}

.navbar:not(.navbar--scrolled) .cta-btn-icon {
  color: rgba(30, 41, 59, 0.7);
}

.lang-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 36px;
  padding: 0 0.8rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 50px;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.88rem;
  color: rgba(230, 240, 250, 0.7);
  transition: all 0.35s ease;
}

.lang-btn:hover {
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
}

.lang-chevron {
  transition: transform 0.2s;
  flex-shrink: 0;
}

.lang-chevron.is-open {
  transform: rotate(180deg);
}

.navbar:not(.navbar--scrolled) .lang-btn {
  color: rgba(30, 41, 59, 0.7);
}

.lang-picker,
.cta-group {
  position: relative;
}

.lang-dropdown,
.cta-dropdown,
.mobile-drawer {
  position: absolute;
  background: rgba(20, 25, 35, 0.98);
  border: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5), 0 4px 12px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(16px) saturate(180%);
  -webkit-backdrop-filter: blur(24px) saturate(150%);
}

.lang-dropdown,
.cta-dropdown {
  top: calc(100% + 14px);
  right: 0;
  border-radius: 16px;
  padding: 0.5rem;
}

.mobile-drawer {
  top: calc(100% + 10px);
  left: 0;
  right: 0;
  border-radius: 20px;
  padding: 0.5rem 1.25rem 1.5rem;
}

.navbar:not(.navbar--scrolled) .lang-dropdown,
.navbar:not(.navbar--scrolled) .cta-dropdown,
.navbar:not(.navbar--scrolled) .mobile-drawer {
  background: rgba(245, 248, 252, 0.98);
  border-color: rgba(30, 41, 59, 0.15);
}

.cta-dropdown {
  min-width: 260px;
}

.cta-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0.7rem 0.75rem;
  border-radius: 12px;
  text-decoration: none;
  transition: background 0.15s;
  cursor: pointer;
}

.cta-item:hover {
  background: rgba(255, 255, 255, 0.08);
}

.navbar:not(.navbar--scrolled) .cta-item:hover {
  background: rgba(30, 41, 59, 0.05);
}

.cta-item__icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.09);
  color: #fff;
}

.navbar:not(.navbar--scrolled) .cta-item__icon {
  background: rgba(30, 41, 59, 0.05);
  border-color: rgba(30, 41, 59, 0.1);
  color: #334155;
}

.cta-item__label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #fff;
  line-height: 1.2;
}

.cta-item__sub {
  font-size: 0.75rem;
  color: rgba(220, 230, 240, 0.7);
  margin-top: 2px;
}

.navbar:not(.navbar--scrolled) .cta-item__label {
  color: #1e293b;
}

.navbar:not(.navbar--scrolled) .cta-item__sub {
  color: #475569;
}

.cta-item--muted .cta-item__label {
  color: rgba(255, 255, 255, 0.8);
}

.cta-item--muted .cta-item__sub {
  color: rgba(220, 230, 240, 0.5);
}

.navbar:not(.navbar--scrolled) .cta-item--muted .cta-item__label {
  color: #334155;
}

.navbar:not(.navbar--scrolled) .cta-item--muted .cta-item__sub {
  color: #64748b;
}

.cta-sep {
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
  margin: 0.3rem 0.5rem;
}

.navbar:not(.navbar--scrolled) .cta-sep {
  background: rgba(30, 41, 59, 0.1);
}

.lang-dropdown {
  min-width: 120px;
}

.lang-dropdown--right {
  right: 0;
}

.lang-option {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 0.48rem 0.65rem;
  border: none;
  background: transparent;
  border-radius: 10px;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.83rem;
  font-weight: 500;
  color: rgba(230, 240, 250, 0.8);
  transition: all 0.14s;
  text-align: left;
}

.lang-option:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
}

.navbar:not(.navbar--scrolled) .lang-option {
  color: #334155;
}

.navbar:not(.navbar--scrolled) .lang-option:hover {
  background: rgba(30, 41, 59, 0.05);
  color: #0f172a;
}

.lang-option.is-active {
  color: #8dbdd8;
}

.lang-tick {
  margin-left: auto;
  color: var(--accent);
}

/* Hamburger */
.navbar__mobile-right {
  display: none;
  align-items: center;
  gap: 0.8rem;
  margin-left: auto;
}

.hamburger {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 5px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  cursor: pointer;
  transition: all 0.18s;
}

.hamburger:hover {
  background: rgba(255, 255, 255, 0.15);
}

.navbar:not(.navbar--scrolled) .hamburger {
  background: rgba(30, 41, 59, 0.05);
  border-color: rgba(30, 41, 59, 0.1);
}

.hamburger__bar {
  display: block;
  width: 16px;
  height: 1.5px;
  border-radius: 2px;
  background: #fff;
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s;
}

.navbar:not(.navbar--scrolled) .hamburger__bar {
  background: #1e293b;
}

.hamburger.is-open .hamburger__bar:nth-child(1) {
  transform: translateY(3.25px) rotate(45deg);
}

.hamburger.is-open .hamburger__bar:nth-child(2) {
  transform: translateY(-3.25px) rotate(-45deg);
}

.drawer-link {
  display: block;
  padding: 0.8rem 0;
  font-size: 0.93rem;
  font-weight: 500;
  color: rgba(230, 240, 250, 0.8);
  text-decoration: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  transition: color 0.16s;
}

.drawer-link:last-child {
  border-bottom: none;
}

.drawer-link:hover,
.drawer-link.router-link-active {
  color: #fff;
}

.drawer-link--dim {
  color: rgba(200, 210, 220, 0.6);
}

.drawer-link--blue {
  color: #7aaecc;
  font-weight: 600;
}

.drawer-link--blue:hover {
  color: #9dc7de;
}

.drawer-sep {
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
  margin: 0.35rem 0;
}

.navbar:not(.navbar--scrolled) .drawer-link {
  color: #334155;
  border-bottom-color: rgba(30, 41, 59, 0.1);
}

.navbar:not(.navbar--scrolled) .drawer-link:hover,
.navbar:not(.navbar--scrolled) .drawer-link.router-link-active {
  color: #0f172a;
}

.navbar:not(.navbar--scrolled) .drawer-link--dim {
  color: #64748b;
}

.navbar:not(.navbar--scrolled) .drawer-sep {
  background: rgba(30, 41, 59, 0.1);
}

@media (max-width: 860px) {

  .navbar__links,
  .navbar__right {
    display: none;
  }

  .navbar__mobile-right {
    display: flex;
  }
}

/* Light Mode Overrides */
.navbar.navbar--dark-page:not(.navbar--scrolled) .navbar__logo-img {
  filter: none !important;
}

.navbar.navbar--dark-page:not(.navbar--scrolled) .navbar__link {
  color: rgba(230, 240, 250, 0.7) !important;
}

.navbar.navbar--dark-page:not(.navbar--scrolled) .navbar__link:hover,
.navbar.navbar--dark-page:not(.navbar--scrolled) .navbar__link.router-link-active {
  color: #fff !important;
}

.navbar.navbar--dark-page:not(.navbar--scrolled) .btn-ghost {
  color: rgba(230, 240, 250, 0.85) !important;
  border-color: transparent !important;
  background: transparent !important;
}

.navbar.navbar--dark-page:not(.navbar--scrolled) .btn-ghost:hover {
  background: rgba(255, 255, 255, 0.1) !important;
  color: #fff !important;
}
.navbar:not(.navbar--scrolled) .btn-profile-icon {
  color: rgba(30, 41, 59, 0.85);
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(30, 41, 59, 0.2);
}

.navbar.navbar--dark-page:not(.navbar--scrolled) .cta-btn-icon,
.navbar.navbar--dark-page:not(.navbar--scrolled) .lang-btn {
  color: rgba(230, 240, 250, 0.7) !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
  background: rgba(255, 255, 255, 0.05) !important;
}

.navbar.navbar--dark-page:not(.navbar--scrolled) .cta-btn-icon:hover,
.navbar.navbar--dark-page:not(.navbar--scrolled) .lang-btn:hover {
  color: #fff !important;
  background: rgba(255, 255, 255, 0.15) !important;
}

.navbar.navbar--dark-page:not(.navbar--scrolled) .hamburger {
  border-color: rgba(255, 255, 255, 0.08) !important;
  background: rgba(255, 255, 255, 0.05) !important;
}

.navbar.navbar--dark-page:not(.navbar--scrolled) .hamburger__bar {
  background: #fff !important;
}

.navbar.navbar--dark-page:not(.navbar--scrolled) .lang-dropdown,
.navbar.navbar--dark-page:not(.navbar--scrolled) .cta-dropdown,
.navbar.navbar--dark-page:not(.navbar--scrolled) .mobile-drawer {
  background: rgba(20, 25, 35, 0.98) !important;
  border-color: rgba(255, 255, 255, 0.15) !important;
}

.navbar.navbar--dark-page:not(.navbar--scrolled) .lang-option {
  color: rgba(230, 240, 250, 0.8) !important;
}

.navbar.navbar--dark-page:not(.navbar--scrolled) .lang-option:hover {
  color: #fff !important;
  background: rgba(255, 255, 255, 0.08) !important;
}

.navbar.navbar--dark-page:not(.navbar--scrolled) .cta-item__label {
  color: #fff !important;
}

.navbar.navbar--dark-page:not(.navbar--scrolled) .cta-item__sub {
  color: rgba(220, 230, 240, 0.7) !important;
}

.navbar.navbar--dark-page:not(.navbar--scrolled) .cta-item:hover {
  background: rgba(255, 255, 255, 0.08) !important;
}

.navbar.navbar--dark-page:not(.navbar--scrolled) .cta-item__icon {
  background: rgba(255, 255, 255, 0.06) !important;
  border-color: rgba(255, 255, 255, 0.09) !important;
  color: #fff !important;
}

.navbar.navbar--dark-page:not(.navbar--scrolled) .drawer-link {
  color: rgba(230, 240, 250, 0.8) !important;
  border-bottom-color: rgba(255, 255, 255, 0.08) !important;
}

.navbar.navbar--dark-page:not(.navbar--scrolled) .drawer-link:hover {
  color: #fff !important;
}

.navbar.navbar--dark-page:not(.navbar--scrolled) .drawer-sep {
  background: rgba(255, 255, 255, 0.1) !important;
}

.navbar.navbar--dark-page:not(.navbar--scrolled) .mobile-login-btn {
  color: rgba(230, 240, 250, 0.9) !important;
  background: rgba(255, 255, 255, 0.08) !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
}

/* Transitions & Utils */
.drop-enter-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.drop-leave-active {
  transition: opacity 0.1s ease, transform 0.1s ease;
}

.drop-enter-from,
.drop-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.drawer-enter-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.drawer-leave-active {
  transition: opacity 0.14s ease, transform 0.14s ease;
}

.drawer-enter-from,
.drawer-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.whatsapp-float {
  position: fixed;
  bottom: 5rem;
  left: 1.5rem;
  z-index: 49;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: #162e20;
  border: 1px solid rgba(37, 211, 102, 0.25);
  color: #25d366;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  transition: transform 0.24s, box-shadow 0.24s;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
}

.whatsapp-float:hover {
  transform: translateY(-3px) scale(1.08);
  box-shadow: 0 8px 24px rgba(37, 211, 102, 0.18);
}

.mobile-sticky {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 48;
  padding: 0.75rem 1rem;
  background: rgba(6, 9, 18, 0.96);
  border-top: 1px solid rgba(74, 127, 165, 0.2);
  backdrop-filter: blur(12px);
  display: none;
}

@media (max-width: 768px) {
  .mobile-sticky {
    display: block;
  }

  .scroll-top {
    bottom: 4.5rem !important;
  }

  .whatsapp-float {
    bottom: 4.5rem;
  }
}

.mobile-sticky__btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0.82rem;
  border-radius: 10px;
  background: #4a7fa5;
  color: #fff;
  font-weight: 700;
  font-size: 0.9rem;
  text-decoration: none;
  transition: background 0.2s;
}

.mobile-sticky__btn:hover {
  background: #5a90b8;
}

.slide-up-enter-active {
  transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.25s;
}

.slide-up-leave-active {
  transition: transform 0.2s ease, opacity 0.18s;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

.scroll-top {
  position: fixed;
  z-index: 50;
  bottom: 2rem;
  right: 2rem;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: rgba(8, 12, 24, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.06);
  color: rgba(180, 200, 220, 0.45);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.22s;
  backdrop-filter: blur(12px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.scroll-top:hover {
  color: #e0eaf4;
  transform: translateY(-2px);
}

/* Footer */
.site-footer {
  background: #04060e;
  border-top: 1px solid rgba(255, 255, 255, 0.04);
}

.footer-inner {
  max-width: 1280px;
  margin: 0 auto;
  padding: 3.5rem 2.5rem 2.5rem;
}

.footer-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  margin-bottom: 2.5rem;
}

@media (max-width: 768px) {
  .footer-grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 480px) {
  .footer-grid {
    grid-template-columns: 1fr;
  }
}

.footer-logo {
  height: 28px;
  width: auto;
  margin-bottom: 0.9rem;
}

.footer-desc {
  font-size: 0.83rem;
  color: rgba(120, 140, 165, 0.65);
  margin-bottom: 1rem;
  line-height: 1.6;
}

.footer-cta-link {
  display: inline-block;
  font-size: 0.8rem;
  font-weight: 700;
  color: #6499bb;
  text-decoration: none;
  letter-spacing: 0.01em;
  padding: 0.35rem 0.8rem;
  border: 1px solid rgba(74, 127, 165, 0.25);
  border-radius: 7px;
  transition: all 0.18s;
}

.footer-cta-link:hover {
  border-color: rgba(74, 127, 165, 0.5);
  color: #7fb3cc;
}

.footer-heading {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(120, 140, 165, 0.45);
  margin-bottom: 1rem;
}

.footer-link {
  display: block;
  font-size: 0.85rem;
  color: rgba(140, 165, 190, 0.55);
  text-decoration: none;
  margin-bottom: 0.6rem;
  transition: color 0.18s;
}

.footer-link:hover {
  color: rgba(210, 225, 240, 0.85);
}

.footer-social {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.2rem;
}

.footer-social a {
  font-size: 1rem;
  color: rgba(120, 145, 170, 0.5);
  text-decoration: none;
  transition: color 0.18s;
}

.footer-social a:hover {
  color: rgba(200, 220, 235, 0.8);
}

.footer-login-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  color: rgba(100, 153, 187, 0.7);
  text-decoration: none;
  transition: color 0.18s;
}

.footer-login-link:hover {
  color: #8dbdd8;
}

.footer-bottom {
  border-top: 1px solid rgba(255, 255, 255, 0.04);
  padding-top: 1.75rem;
  text-align: center;
  font-size: 0.78rem;
  color: rgba(100, 120, 145, 0.45);
}

/* Cookie */
.cookie-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  padding: 1rem;
  pointer-events: none;
}

.cookie-inner {
  max-width: 880px;
  margin: 0 auto;
  pointer-events: auto;
  background: rgba(8, 12, 24, 0.96);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 14px;
  padding: 1.1rem 1.4rem;
  backdrop-filter: blur(20px);
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.cookie-text {
  flex: 1;
  min-width: 200px;
}

.cookie-text strong {
  display: block;
  font-size: 0.88rem;
  color: rgba(210, 225, 240, 0.85);
  margin-bottom: 0.2rem;
}

.cookie-text span {
  font-size: 0.82rem;
  color: rgba(140, 165, 190, 0.6);
}

.cookie-text a {
  color: #6499bb;
  text-decoration: none;
}

.cookie-text a:hover {
  text-decoration: underline;
}

.cookie-actions {
  display: flex;
  gap: 0.5rem;
}

.cookie-btn {
  padding: 0.45rem 1rem;
  border-radius: 8px;
  font-size: 0.83rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.18s;
  border: none;
}

.cookie-btn--ghost {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.09);
  color: rgba(160, 185, 210, 0.65);
}

.cookie-btn--ghost:hover {
  background: rgba(255, 255, 255, 0.05);
  color: rgba(210, 225, 240, 0.85);
}

.cookie-btn--solid {
  background: #4a7fa5;
  color: #fff;
}

.cookie-btn--solid:hover {
  background: #5a90b8;
}
</style>